/**
 * Cart API service
 * Endpoints: POST /cart, POST /cart/bundle, GET /cart, POST /cart/line,
 * POST /cart/line/variant, POST /cart/coupon, DELETE /cart/coupon
 */

import type { Result } from "@/types/layout";
import type {
  Cart,
  AddToCartRequest,
  AddBundleToCartRequest,
  UpdateLineRequest,
  SetLineVariantRequest,
  CouponRequest,
} from "@/types/cart";
import { getJson, postJson, deleteJson } from "./http";
import { getEnv } from "@/config/env";

function cartUrl(path = ""): string {
  return `${getEnv().apiBaseUrl}/cart${path}`;
}

const CART_OPTIONS: RequestInit = {
  credentials: "include",
};

export async function addToCart(
  productId: string,
  quantity = 1,
  variantId?: string
): Promise<Result<Cart>> {
  const body: AddToCartRequest = {
    productId,
    quantity,
    ...(variantId ? { variantId } : {}),
  };
  return postJson<Cart>(cartUrl(), body, CART_OPTIONS);
}

/**
 * Adds a bundle: the backend resolves the bundle document, then adds one order
 * line per included product, all stamped with a shared bundle id. The bundle
 * itself never becomes a line — it has no SKU.
 */
export async function addBundleToCart(
  bundleProductId: string,
  quantity = 1,
  variants?: AddBundleToCartRequest["variants"]
): Promise<Result<Cart>> {
  const body: AddBundleToCartRequest = {
    bundleProductId,
    quantity,
    ...(variants?.length ? { variants } : {}),
  };
  return postJson<Cart>(cartUrl("/bundle"), body, CART_OPTIONS);
}

export async function fetchCart(signal?: AbortSignal): Promise<Result<Cart>> {
  // Cart is per-user → must never be served stale. After an order the backend
  // clears it, but navigation is a full-page reload and the browser/CDN would
  // otherwise serve a cached pre-order response (navbar shows phantom items
  // until the cache expires). `nocache` skips the backend output cache; the
  // unique timestamp gives each request a distinct URL so browser/CDN URL caches
  // always miss; `cache: "no-store"` covers the browser HTTP cache.
  const url = `${cartUrl()}?nocache&_=${Date.now()}`;
  return getJson<Cart>(url, {
    ...CART_OPTIONS,
    cache: "no-store",
    ...(signal ? { signal } : {}),
  });
}

export async function updateCartLine(
  orderLineId: string,
  quantity: number
): Promise<Result<Cart>> {
  const body: UpdateLineRequest = { orderLineId, quantity };
  return postJson<Cart>(cartUrl("/line"), body, CART_OPTIONS);
}

/**
 * Picks the version (size, colour) of a line already in the cart. Needed because
 * a bundle added from a product card arrives with none chosen for its variable
 * items, and checkout is blocked until every such line names one.
 */
export async function setCartLineVariant(
  orderLineId: string,
  variantId: string
): Promise<Result<Cart>> {
  const body: SetLineVariantRequest = { orderLineId, variantId };
  return postJson<Cart>(cartUrl("/line/variant"), body, CART_OPTIONS);
}

export async function addCoupon(code: string): Promise<Result<Cart>> {
  const body: CouponRequest = { code };
  return postJson<Cart>(cartUrl("/coupon"), body, CART_OPTIONS);
}

export async function removeCoupon(code: string): Promise<Result<Cart>> {
  const body: CouponRequest = { code };
  return deleteJson<Cart>(cartUrl("/coupon"), body, CART_OPTIONS);
}
